/**
 * Navigation model.
 *
 * Transcribed from the prototype's `navFor(tier)`, which is the design of record. Two things
 * here are not obvious from the README and were wrong in the first pass of this shell:
 *
 * 1. **Manage signals (the saved-search CRUD list) is NOT a nav destination.** It is reached
 *    from the Signals surface via "Manage signals". Putting it in the nav invents an IA the
 *    design deliberately does not have.
 * 2. **On a phone the bar shows the first four main entries plus "More"** — not a fixed set
 *    of tabs. Everything past the fourth entry, plus every locked entry, lives on the More
 *    screen. At Snapshot that means just Towns, Account and More.
 *
 * Locked entries are never hidden. They render in their own group and route to /pricing.
 */
import type { IconName } from '@/components/Icon';
import type { Tier } from '@/lib/tier';

export interface NavEntry {
  id: string;
  to: string;
  /** Sidebar label. */
  label: string;
  /** Phone tab-bar label — deliberately shorter (e.g. "Leg. History"). */
  tab: string;
  icon: IconName;
}

const ENTRIES = {
  feed: { id: 'feed', to: '/feed', label: 'My Feed', tab: 'Feed', icon: 'towns' },
  signal: { id: 'signal', to: '/signals', label: 'Signals', tab: 'Signals', icon: 'feed' },
  search: { id: 'search', to: '/search', label: 'Search', tab: 'Search', icon: 'search' },
  project: {
    id: 'project',
    to: '/legislative-history',
    label: 'Legislative History',
    tab: 'Leg. History',
    icon: 'history',
  },
  trends: { id: 'trends', to: '/topic-trends', label: 'Topic Trends', tab: 'Trends', icon: 'trends' },
  ask: { id: 'ask', to: '/ask', label: 'Ask', tab: 'Ask', icon: 'ask' },
  account: { id: 'account', to: '/account', label: 'Account', tab: 'Account', icon: 'account' },
} as const satisfies Record<string, NavEntry>;

type EntryKey = keyof typeof ENTRIES;

/** Order per tier, verbatim from the prototype. */
const ORDER: Record<Tier, { main: readonly EntryKey[]; locked: readonly EntryKey[] }> = {
  snapshot: {
    main: ['feed', 'account'],
    locked: ['signal', 'search', 'ask', 'project', 'trends'],
  },
  intelligence: {
    main: ['feed', 'signal', 'search', 'account'],
    locked: ['ask', 'project', 'trends'],
  },
  pro: {
    main: ['feed', 'signal', 'search', 'project', 'trends', 'ask', 'account'],
    locked: [],
  },
};

/** How many main entries fit in the phone tab bar before "More" takes over. */
export const PHONE_TABS = 4;

export interface NavModel {
  main: NavEntry[];
  locked: NavEntry[];
  /** Main entries that did not fit in the phone tab bar, plus every locked entry. */
  moreRows: { entry: NavEntry; locked: boolean }[];
}

export function navFor(tier: Tier): NavModel {
  const order = ORDER[tier];
  const main = order.main.map((k) => ENTRIES[k]);
  const locked = order.locked.map((k) => ENTRIES[k]);

  return {
    main,
    locked,
    moreRows: [
      ...main.slice(PHONE_TABS).map((entry) => ({ entry, locked: false })),
      ...locked.map((entry) => ({ entry, locked: true })),
    ],
  };
}
